import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useFeatures } from "@/lib/useFeatures";
import { FeatureLocked, PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Trash2, ExternalLink, Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { uploadFiles, fileUrl } from "@/lib/upload";

export default function Website() {
  const { rid } = useParams();
  const features = useFeatures(rid);
  const [settings, setSettings] = useState(null);
  const [rest, setRest] = useState(null);
  const [posts, setPosts] = useState([]);
  const [tables, setTables] = useState([]);
  const [draft, setDraft] = useState({ title: "", body: "", url: "", images: [], is_published: true });
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get(`/restaurants/${rid}/settings`).then((r) => setSettings(r.data)).catch(() => {});
    api.get(`/restaurants/${rid}/newsfeed`).then((r) => setPosts(r.data)).catch(() => {});
    api.get(`/restaurants/${rid}/tables`).then((r) => setTables(r.data)).catch(() => {});
    api.get(`/restaurants/${rid}`).then((r) => setRest(r.data)).catch(() => {});
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  if (features && !features.WEBSITE) return <FeatureLocked label="Customer Website" />;
  if (!settings) return null;

  const siteUrl = `${window.location.origin}/site/${rid}`;
  const prettyUrl = rest?.slug ? `${window.location.origin}/s/${rest.slug}` : siteUrl;
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const saveBranding = async () => {
    await api.put(`/restaurants/${rid}/settings`, {
      website_enabled: settings.website_enabled, website_tagline: settings.website_tagline,
      website_primary_color: settings.website_primary_color, website_hero_image: settings.website_hero_image,
      website_logo: settings.website_logo, custom_domain: settings.custom_domain,
      table_order_enabled: settings.table_order_enabled,
    });
    toast.success("Website settings saved");
  };
  const uploadTo = async (field, files) => {
    if (!files?.length) return;
    setUploading(true);
    try { const [url] = await uploadFiles(rid, [files[0]]); setSettings((s) => ({ ...s, [field]: url })); toast.success("Image uploaded — remember to save"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };
  const uploadPostImages = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try { const urls = await uploadFiles(rid, files); setDraft((d) => ({ ...d, images: [...d.images, ...urls] })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };
  const addPost = async () => {
    if (!draft.title) return toast.error("Enter a title");
    await api.post(`/restaurants/${rid}/newsfeed`, draft);
    toast.success("Post published"); setDraft({ title: "", body: "", url: "", images: [], is_published: true }); load();
  };
  const delPost = async (id) => { await api.delete(`/newsfeed/${id}`); load(); };

  return (
    <div>
      <PageHeader title="Customer website" subtitle="Your public site auto-syncs your menu. Take online bookings & orders and post promotions.">
        <Button variant="outline" className="h-11 gap-2" onClick={() => window.open(siteUrl, "_blank")} data-testid="view-site"><ExternalLink className="h-4 w-4" /> View site</Button>
      </PageHeader>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <Card className="p-6 rounded-2xl space-y-4">
          <div className="flex items-center gap-3">
            <Switch checked={!!settings.website_enabled} onCheckedChange={(v) => setSettings({ ...settings, website_enabled: v })} data-testid="website-enabled" />
            <span className="text-sm">Website live</span>
          </div>
          <div className="rounded-lg bg-secondary p-3 text-xs break-all">Public URL: <a href={prettyUrl} target="_blank" rel="noreferrer" className="text-primary" data-testid="site-url">{prettyUrl}</a></div>
          <div><Label>Tagline</Label><Input className="h-11 mt-1.5" value={settings.website_tagline || ""} onChange={(e) => setSettings({ ...settings, website_tagline: e.target.value })} data-testid="website-tagline" /></div>
          <div><Label>Brand colour</Label><Input type="color" className="h-11 mt-1.5 w-24" value={settings.website_primary_color || "#0a1a3f"} onChange={(e) => setSettings({ ...settings, website_primary_color: e.target.value })} data-testid="website-color" /></div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Logo</Label>
              <div className="mt-1.5 flex items-center gap-3">
                {settings.website_logo
                  ? <img src={fileUrl(settings.website_logo)} alt="logo" className="h-14 w-14 rounded-xl object-cover border border-border" data-testid="logo-preview" />
                  : <div className="h-14 w-14 rounded-xl bg-secondary flex items-center justify-center text-muted-foreground"><Upload className="h-5 w-5" /></div>}
                <label className="cursor-pointer text-sm text-primary font-medium">
                  <input type="file" accept="image/*" className="hidden" data-testid="logo-upload" onChange={(e) => uploadTo("website_logo", e.target.files)} />
                  {settings.website_logo ? "Change" : "Upload logo"}
                </label>
                {settings.website_logo && <button onClick={() => setSettings({ ...settings, website_logo: null })} className="text-xs text-red-500">Remove</button>}
              </div>
            </div>
            <div>
              <Label>Banner image</Label>
              <div className="mt-1.5 flex items-center gap-3">
                {settings.website_hero_image
                  ? <img src={fileUrl(settings.website_hero_image)} alt="banner" className="h-14 w-24 rounded-xl object-cover border border-border" data-testid="banner-preview" />
                  : <div className="h-14 w-24 rounded-xl bg-secondary flex items-center justify-center text-muted-foreground"><Upload className="h-5 w-5" /></div>}
                <label className="cursor-pointer text-sm text-primary font-medium">
                  <input type="file" accept="image/*" className="hidden" data-testid="banner-upload" onChange={(e) => uploadTo("website_hero_image", e.target.files)} />
                  {settings.website_hero_image ? "Change" : "Upload banner"}
                </label>
                {settings.website_hero_image && <button onClick={() => setSettings({ ...settings, website_hero_image: null })} className="text-xs text-red-500">Remove</button>}
              </div>
            </div>
          </div>
          {uploading && <p className="text-xs text-muted-foreground">Uploading…</p>}

          <div className="border-t border-border pt-4">
            <Label>Custom domain</Label>
            <Input className="h-11 mt-1.5" placeholder="e.g. order.yourrestaurant.co.uk" value={settings.custom_domain || ""} onChange={(e) => setSettings({ ...settings, custom_domain: e.target.value })} data-testid="custom-domain" />
            <div className="rounded-lg bg-secondary p-3 text-xs text-muted-foreground mt-2">
              To use your own domain, log in to your domain registrar (GoDaddy, Namecheap, etc.) and set up <b>URL forwarding with masking</b> pointing to your DineFlo link above (<span className="break-all">{prettyUrl}</span>). Your customers will see your domain while the site is served by DineFlo. For a fully hosted custom domain with HTTPS, contact DineFlo support.
            </div>
          </div>
          <Button className="h-11" onClick={saveBranding} data-testid="save-website">Save website settings</Button>
        </Card>

        <Card className="p-6 rounded-2xl">
          <p className="font-display font-semibold mb-3">Newsfeed / promotions</p>
          <div className="space-y-2">
            <Input placeholder="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="post-title" />
            <Textarea placeholder="Description" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} data-testid="post-body" />
            <Input placeholder="Link URL (optional, opens on click)" value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} data-testid="post-url" />
            <div>
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-primary font-medium">
                <input type="file" accept="image/*" multiple className="hidden" data-testid="post-images-upload" onChange={(e) => uploadPostImages(e.target.files)} />
                <Upload className="h-4 w-4" /> Add images {draft.images.length > 0 && `(${draft.images.length})`}
              </label>
              {draft.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="post-image-thumbs">
                  {draft.images.map((im, i) => (
                    <div key={i} className="relative">
                      <img src={fileUrl(im)} alt="" className="h-16 w-16 rounded-lg object-cover border border-border" />
                      <button onClick={() => setDraft({ ...draft, images: draft.images.filter((_, j) => j !== i) })}
                        className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button className="h-11 gap-1" onClick={addPost} data-testid="add-post"><Plus className="h-4 w-4" /> Publish post</Button>
          </div>
          <div className="mt-4 space-y-2">
            {posts.map((p) => (
              <div key={p.id} data-testid={`post-${p.id}`} className="flex items-center justify-between rounded-lg border border-border p-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{p.title}</p>
                  {p.url && <p className="text-xs text-primary truncate">{p.url}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={p.is_published ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}>{p.is_published ? "Live" : "Draft"}</Badge>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => delPost(p.id)} data-testid={`del-post-${p.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </div>
            ))}
            {posts.length === 0 && <p className="text-sm text-muted-foreground">No posts yet.</p>}
          </div>
        </Card>

        <Card className="p-6 rounded-2xl lg:col-span-2" data-testid="qr-card">
          <div className="flex items-center gap-3 mb-1">
            <p className="font-display font-semibold">QR ordering for dine-in tables</p>
            <div className="flex items-center gap-2 ml-auto">
              <Switch checked={settings.table_order_enabled !== false} onCheckedChange={(v) => setSettings({ ...settings, table_order_enabled: v })} data-testid="table-order-enabled" />
              <span className="text-sm text-muted-foreground">Enabled</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Print a QR for each table. Customers scan it to view your menu, add notes & allergens, and send an order straight to your kitchen — assigned to that table. Save settings after toggling.</p>
          {tables.length === 0 && <p className="text-sm text-muted-foreground">No tables yet — add tables in the Tables page.</p>}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {tables.map((t) => {
              const qr = `${apiBase}/restaurants/${rid}/tables/${t.id}/qr?base=${encodeURIComponent(window.location.origin)}`;
              return (
                <div key={t.id} data-testid={`qr-table-${t.id}`} className="rounded-xl border border-border p-3 text-center">
                  <img src={qr} alt={`QR ${t.name || t.number}`} className="w-full aspect-square object-contain bg-white rounded-lg" />
                  <p className="font-medium text-sm mt-2">{t.name || `Table ${t.number}`}</p>
                  <button onClick={() => window.open(qr, "_blank")} data-testid={`qr-print-${t.id}`} className="text-xs text-primary font-medium mt-1">Open / print</button>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
